// GuildLootLedgerTab.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Officer/owner view of every loot award the guild has ever recorded, with
// the ability to delete (revoke) an award. Revoked awards stay visible
// (greyed out with audit info) so the council always has a record of
// what was undone and why.
//
// Backend contract: GET /guilds/:guildId/loot-ledger  (in loot_award_routes.js)
//                   DELETE /guilds/:guildId/loot-awards/:awardId
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "./config";
import { wowheadItemUrl } from "./wowhead";
import { useConfirm } from "./ConfirmDialog";
import { useToast } from "./Toast";

import { tc } from "./theme/legible";
type LedgerRow = {
  id: string;
  guild_id: string;
  guild_raid_id: string | null;
  session_id: string | null;
  character_id: string | null;
  character_name: string;
  external_item_id: string;
  loot_item_id: number | null;
  awarded_at: string;
  source: string;
  response: string | null;
  votes: number | null;
  bis_tag_at_award: string | null;
  created_at: string;
  item_name: string | null;
  item_slot: string | null;
  item_quality: string | null;
  item_boss_name: string | null;
  raid_name: string | null;
  revoked: boolean;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_by_name: string | null;
  revoke_reason: string | null;
};

type LedgerResponse = {
  guild_id: string;
  count: number;
  limit: number;
  next_cursor: string | null;
  awards: LedgerRow[];
};

type Props = {
  guildId: string;
  gameCode: string | null;
};

const QUALITY_HEX: Record<string, string> = {
  poor: "#9d9d9d", common: "#ffffff", uncommon: "#1eff00",
  rare: "#0070dd", epic: "#a335ee", legendary: "#ff8000",
  "0": "#9d9d9d", "1": "#ffffff", "2": "#1eff00",
  "3": "#0070dd", "4": "#a335ee", "5": "#ff8000",
};
function qualityColor(q: string | null): string {
  if (!q) return "#cbd5e1";
  return QUALITY_HEX[q.toLowerCase()] || "#cbd5e1";
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function GuildLootLedgerTab({ guildId, gameCode }: Props) {
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showRevoked, setShowRevoked] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const confirm = useConfirm().confirm;
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/guilds/${guildId}/loot-ledger?limit=500&include_revoked=true`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const txt = await res.text();
        console.error("[Ledger]", res.status, txt);
        throw new Error(`HTTP ${res.status}`);
      }
      const data: LedgerResponse = await res.json();
      setRows(Array.isArray(data.awards) ? data.awards : []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "load_failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [guildId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row: LedgerRow) => {
    if (row.revoked) return;
    const ok = await confirm({
      title: `Remove ${row.item_name || "this award"}?`,
      description: `This will remove the award from ${row.character_name}. The award stays in the ledger as a revoked entry with your name and any note you provide. Heat values will be recalculated on the next page load.`,
      confirmText: "Remove award",
      cancelText: "Keep",
      variant: "danger",
    });
    if (!ok) return;

    const reason = window.prompt(
      "Optional note (e.g. \"awarded to wrong character\", \"misclick\"). Leave blank to skip.",
      ""
    );
    // User cancelled the prompt → abort entirely (don't silently delete).
    if (reason === null) return;

    setBusyId(row.id);
    try {
      const res = await fetch(`${API_URL}/guilds/${guildId}/loot-awards/${row.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      showToast(`Removed ${row.item_name || "award"} from ${row.character_name}`, "success");
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "delete_failed";
      showToast(`Failed to remove award: ${msg}`, "error");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showRevoked && r.revoked) return false;
      if (!q) return true;
      return (
        (r.character_name || "").toLowerCase().includes(q) ||
        (r.item_name || "").toLowerCase().includes(q) ||
        (r.raid_name || "").toLowerCase().includes(q) ||
        (r.item_boss_name || "").toLowerCase().includes(q) ||
        (r.external_item_id || "").includes(q)
      );
    });
  }, [rows, search, showRevoked]);

  const activeCount = rows.filter((r) => !r.revoked).length;
  const revokedCount = rows.filter((r) => r.revoked).length;

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-[var(--c-ink)] tracking-tight">Loot ledger</h2>
          <p className="text-xs text-[var(--c-ink-3)] mt-0.5">
            Every award the guild has recorded. Remove an award and it stays here as a revoked entry with the officer's name and an optional note.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-md border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider text-[var(--c-ink-3)]">
            <span className="text-emerald-800/85 dark:text-emerald-200/85 tabular-nums">{activeCount}</span> active
          </div>
          <div className="rounded-md border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider text-[var(--c-ink-3)]">
            <span className="text-red-800/85 dark:text-red-200/85 tabular-nums">{revokedCount}</span> revoked
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search character, item, boss, or item ID…"
          className="flex-1 min-w-[200px] rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:border-cyan-400/40 focus:outline-none"
        />
        <label className="flex items-center gap-2 rounded-lg border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[var(--c-ink-2)] cursor-pointer hover:bg-[var(--c-sunken)]">
          <input
            type="checkbox"
            checked={showRevoked}
            onChange={(e) => setShowRevoked(e.target.checked)}
            className="size-3.5 accent-red-400"
          />
          Show revoked
        </label>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"
        >
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="py-12 text-center text-xs text-[var(--c-ink-3)] uppercase tracking-[0.2em]">
          Loading ledger…
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <div className="text-2xl mb-2 opacity-60">⚠️</div>
          <div className="text-sm text-[var(--c-ink-3)]">Failed to load ledger ({error}).</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)]">
          <div className="text-2xl mb-2 opacity-30">🗂️</div>
          <div className="text-xs text-[var(--c-ink-4)]">
            {rows.length === 0 ? "No awards recorded yet." : "No awards match the current filter."}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] overflow-hidden">
          {/* Header row (desktop only) */}
          <div className="hidden md:grid grid-cols-[1fr_1fr_120px_140px_100px_88px] gap-3 px-4 py-2 text-[10px] uppercase font-bold tracking-wider text-[var(--c-ink-3)] bg-[var(--c-bg-veil)] border-b border-[var(--c-line-soft)]">
            <div>Item</div>
            <div>Awarded to</div>
            <div>Raid</div>
            <div>When</div>
            <div>Source</div>
            <div className="text-right">Action</div>
          </div>

          <ul className="divide-y divide-[var(--c-line-soft)] list-none m-0 p-0">
            {filtered.map((r) => {
              const isRevoked = r.revoked;
              const qcolor = qualityColor(r.item_quality);
              const itemHref = r.external_item_id
                ? wowheadItemUrl(r.external_item_id, gameCode)
                : null;

              return (
                <li
                  key={r.id}
                  className={`grid md:grid-cols-[1fr_1fr_120px_140px_100px_88px] gap-3 px-4 py-3 items-center text-xs ${
                    isRevoked ? "bg-red-500/10 dark:bg-red-950/[0.08] text-[var(--c-ink-3)]" : "text-[var(--c-ink)] hover:bg-[var(--c-bg-veil)]"
                  }`}
                >
                  {/* Item */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {itemHref ? (
                        
                         <a href={itemHref}
                          target="_blank"
                          rel="noreferrer"
                          data-wh-rename-link="false"
                          className={`font-semibold truncate ${isRevoked ? "line-through" : ""}`}
                          style={{ color: tc(isRevoked ? "#71717a" : qcolor) }}
                        >
                          {r.item_name || `#${r.external_item_id}`}
                        </a>
                      ) : (
                        <span className={`font-semibold ${isRevoked ? "line-through text-[var(--c-ink-3)]" : "text-[var(--c-ink)]"}`}>
                          {r.item_name || `#${r.external_item_id}`}
                        </span>
                      )}
                      {r.bis_tag_at_award ? (
                        <span className="shrink-0 rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800/80 dark:text-amber-200/80">
                          {r.bis_tag_at_award}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[10px] text-[var(--c-ink-3)]">
                      {r.item_slot || "—"}{r.item_boss_name ? ` · ${r.item_boss_name}` : ""}
                    </div>
                    {isRevoked && (r.revoked_by_name || r.revoke_reason) ? (
                      <div className="mt-1 text-[10px] text-red-800/65 dark:text-red-200/65 leading-snug">
                        Revoked{r.revoked_at ? ` ${fmtDate(r.revoked_at)}` : ""}
                        {r.revoked_by_name ? ` by ${r.revoked_by_name}` : ""}
                        {r.revoke_reason ? ` — "${r.revoke_reason}"` : ""}
                      </div>
                    ) : null}
                  </div>

                  {/* Character */}
                  <div className="min-w-0">
                    <div className={`font-semibold truncate ${isRevoked ? "text-[var(--c-ink-3)]" : "text-[var(--c-ink)]"}`}>
                      {r.character_name}
                    </div>
                  </div>

                  {/* Raid */}
                  <div className="text-[11px] text-[var(--c-ink-3)] truncate">
                    {r.raid_name || "—"}
                  </div>

                  {/* When */}
                  <div className="text-[11px] tabular-nums text-[var(--c-ink-3)] whitespace-nowrap">
                    {fmtDate(r.awarded_at)}
                  </div>

                  {/* Source */}
                  <div>
                    <span
                      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                        r.source === "manual"
                          ? "border-cyan-400/30 bg-cyan-500/10 text-cyan-800/85 dark:text-cyan-200/85"
                          : "border-violet-400/30 bg-violet-500/10 text-violet-800/85 dark:text-violet-200/85"
                      }`}
                    >
                      {r.source}
                    </span>
                  </div>

                  {/* Action */}
                  <div className="flex justify-end">
                    {isRevoked ? (
                      <span className="rounded border border-red-500/25 bg-red-500/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-red-800/70 dark:text-red-200/70">
                        Revoked
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        disabled={busyId === r.id}
                        className="rounded border border-red-500/25 bg-red-500/[0.06] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-red-800/85 dark:text-red-200/85 hover:bg-red-500/20 hover:border-red-400/40 disabled:opacity-50 disabled:cursor-wait transition-colors"
                      >
                        {busyId === r.id ? "…" : "Remove"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className="text-[10px] text-[var(--c-ink-4)] leading-relaxed">
        Removing an award deletes it from the active loot history (so heat recalculates) but archives a copy here as a revoked entry. Revoked entries cannot be re-activated from this UI; if you need to restore one, ask an engineer to undo it in the database.
      </p>
    </div>
  );
}